import { Button, cx } from '@otter-ai/den-components';
import { WarningTriangleIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { trackZoom } from '../../tracking/zoom-tracking';
import type { ZoomSyncedAccount } from '../../type/apps';
import { zoomAccountHasError, zoomAccountHasWarning } from '../../utils/zoom';

/**
 * The message to display when the Zoom account is in a warning or error state.
 */
export function ZoomWarningErrorDisplay({
  account,
}: {
  account: ZoomSyncedAccount;
}) {
  const t = useTranslations('settings:integrations.apps.zoom');

  const isWarning = zoomAccountHasWarning(account);
  const isError = zoomAccountHasError(account);

  if (!isWarning && !isError) {
    return null;
  }

  const getErrorText = () => {
    if (account.token_invalid) {
      return t('settings.tokenInvalidError');
    }
    if (account.reconnect_required) {
      return t('settings.reconnectRequiredError');
    }
    return null;
  };

  const errorText = getErrorText();

  const getStep2Action = () => {
    if (account.cloud_sync_enabled) {
      return t('settings.testCreateRecordingButton');
    }
    if (account.live_notes_enabled) {
      return t('settings.testConfigurationButton');
    }
    return t('settings.testConfigurationButton');
  };

  const openConfigurationGuide = () => {
    trackZoom.main.zoomOpenConfiguration(
      account.cloud_sync_enabled ? 'cloud_sync' : 'live_notes',
    );
    let link = t('settings.zoomSetupUrl');

    if (account.cloud_sync_enabled) {
      link = t('settings.zoomSyncUrl');
    }

    if (typeof window !== 'undefined') {
      window.open(link, '_blank');
    }
  };

  const testConfiguration = () => {
    trackZoom.main.zoomTestConfiguration(
      account.cloud_sync_enabled ? 'cloud_sync' : 'live_notes',
    );
    if (account.cloud_sync_enabled) {
      // TODO: Get PMI from the get_connected_accounts API
      window.open(t('settings.zoomMeetingUrl'), '_blank');
    } else if (account.live_notes_enabled) {
      const hasLiveNotesSettingsIssues =
        account.live_notes_setting_issues &&
        Array.isArray(account.live_notes_setting_issues) &&
        account.live_notes_setting_issues.length > 0;
      if (!hasLiveNotesSettingsIssues) {
        // TODO: Update webinar setup tutorial step to 2 after tutorial migration
      }
    }
  };

  return (
    <div
      data-testid="zoom-warning-error-display"
      className={cx(
        'flex flex-col gap-4 p-4 border rounded-lg mt-4',
        isWarning &&
          'border-palette-yellow-400 bg-palette-yellow-100 text-palette-yellow-950',
        isError &&
          'border-palette-red-400 bg-palette-red-100 text-palette-red-950',
      )}
    >
      <div className="flex flex-row gap-3">
        <div className="flex-shrink-0">
          <WarningTriangleIcon
            className={cx(
              'w-5 h-5',
              isWarning && 'text-palette-yellow-800',
              isError && 'text-palette-red-800',
            )}
          />
        </div>
        <div className="flex-1">
          <h6 className="font-semibold text-sm text-gray-900">
            {t('settings.setupTitle')}
          </h6>
          {errorText ? (
            <p className="text-sm text-red-600 mt-2">{errorText}</p>
          ) : (
            <p className="text-sm text-red-600 mt-2">
              {t.rich('settings.configurationIssueError', {
                troubleshootLink: (chunks) => (
                  <a
                    href={t('settings.troubleshootUrl')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {chunks}
                  </a>
                ),
                zoomLink: (chunks) => (
                  <a
                    href={t('settings.zoomUrl')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {chunks}
                  </a>
                ),
              })}
            </p>
          )}
          <div className="flex flex-row gap-2 mt-4">
            <Button
              variant="outlineNeutral"
              version="legacy"
              size="sm"
              onClick={openConfigurationGuide}
            >
              {t('settings.configurationGuideButton')}
            </Button>
            <Button
              variant="outlineNeutral"
              version="legacy"
              size="sm"
              onClick={testConfiguration}
            >
              {getStep2Action()}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
